import React from "react";
import { Link } from "react-router-dom";
import EstatePage from "@/components/sites/EstatePage";
import Reveal from "@/components/sites/Reveal";
import { useBrand, useTenantPath } from "@/lib/brand";
import { mediaUrl } from "@/lib/api";
import { withPreview } from "@/lib/sitePreview";

/**
 * About, in the Estate theme.
 *
 * WHAT THIS PAGE DOES NOT DO
 * --------------------------
 * The classic version writes two paragraphs when the record has none:
 * "We are a small, independent team who believe estate agency should be
 * personal, transparent and evidence-led. The way it used to be, done with
 * the best of what's new." That is a claim about a business nobody here
 * has met, published on their own domain under their own name, and it is
 * on Windlebrook's live site today.
 *
 * So this page is built from what the agency has actually told us: their
 * own elevator pitch, the towns they cover, the schemes that regulate
 * them, and what a client said. An agency with none of a thing gets no
 * section for it.
 */
export default function EstateAbout({ preview = true }) {
  const brand = useBrand();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const p = (href) => withPreview(href, preview);

  const paragraphs = brand.about?.paragraphs
    || (biz.elevator_pitch ? [biz.elevator_pitch] : []);
  const team = brand.team || [];
  const areas = brand.areas_served || [];
  const badges = (brand.regulatory || {}).badges || [];
  const hero = brand.assets?.hero_image_url ? mediaUrl(brand.assets.hero_image_url) : "";
  const quote = brand.testimonial;

  return (
    <EstatePage
      preview={preview}
      title={biz.name ? `About ${biz.name}` : undefined}
      description={paragraphs[0] || undefined}
      eyebrow="Who we are"
      heading={biz.name ? `About ${biz.name}` : "About us"}
      lede={biz.tagline || ""}
      crumbs={[{ label: "About" }]}
    >
      {(paragraphs.length > 0 || hero) && (
        <Reveal>
          <div className="split">
            <div>
              {paragraphs.map((para, i) => (
                <p key={i} style={{ fontSize: 17, lineHeight: 1.7, color: "var(--ink-2)" }}>{para}</p>
              ))}
              {brand.commuter_belt && (
                <p className="note" style={{ marginTop: 8 }}>
                  We work across the {brand.commuter_belt}.
                </p>
              )}
            </div>
            {hero && (
              <figure className="media plate">
                <div className="shot">
                  <img src={hero} alt={brand.assets?.hero_caption || biz.name || ""}
                       loading="lazy" decoding="async" />
                </div>
                {brand.assets?.hero_caption && (
                  <figcaption className="figcap">
                    <span className="what">{brand.assets.hero_caption}</span>
                  </figcaption>
                )}
              </figure>
            )}
          </div>
        </Reveal>
      )}

      {team.length > 0 && (
        <Reveal>
          <div className="section-head" style={{ marginTop: 72 }}>
            <div><h2 className="display-s">The team</h2></div>
          </div>
          <div className="grid cols-3">
            {team.map((person) => (
              <div key={person.name}>
                {person.photo_url && (
                  <img src={mediaUrl(person.photo_url)} alt={person.name}
                       loading="lazy" decoding="async"
                       style={{ aspectRatio: "4/5", objectFit: "cover", width: "100%" }} />
                )}
                <h3 className="display-s" style={{ fontSize: 24, marginTop: 16 }}>{person.name}</h3>
                {person.role && <div className="note">{person.role}</div>}
                {person.bio && <p style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 10 }}>{person.bio}</p>}
              </div>
            ))}
          </div>
        </Reveal>
      )}

      {quote?.quote && (
        <Reveal>
          <div className="split" style={{ marginTop: 72 }}>
            <blockquote>
              “{quote.quote}”
              {quote.attribution && <cite>{quote.attribution}</cite>}
            </blockquote>
            <div />
          </div>
        </Reveal>
      )}

      {areas.length > 0 && (
        <Reveal>
          <div className="section-head" style={{ marginTop: 72 }}>
            <div>
              <div className="eyebrow">Where we work</div>
              <h2 className="display-s" style={{ marginTop: 10 }}>The towns we cover</h2>
            </div>
          </div>
          <div className="pills">
            {areas.map((area) => (
              <Link key={area} className="pill"
                    to={p(tenantPath(`area/${String(area).toLowerCase().replace(/\s+/g, "-")}`))}>
                {area}
              </Link>
            ))}
          </div>
        </Reveal>
      )}

      {badges.length > 0 && (
        <Reveal>
          <div className="section-head" style={{ marginTop: 72 }}>
            <div><h2 className="display-s">How you are protected</h2></div>
          </div>
          <div className="trust">
            {badges.map((badge) => (
              <div key={badge.label}>
                <span className="ic" aria-hidden="true">◆</span>
                <span>{badge.label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      )}
    </EstatePage>
  );
}
